/**
 * Statistiche squadra (V32), la scheda «Squadra». Contano le sole partite
 * concluse e il filtro per competizione restringe tutto, tranne gli
 * allenamenti che sono della stagione.
 *
 * Le card su sfondo grigio vanno a due a due sugli schermi larghi: partite e
 * gol, poi gli eventi a tutta larghezza, poi allenamenti e gol per periodo.
 * Ogni evento ha il suo riquadro con il totale e la media a partita; i gemelli
 * (tiri in porta e fuori, passaggi riusciti e sbagliati, palle recuperate e
 * perse) anche un riquadro di confronto con la quota del primo.
 */

import { useLocation, useParams } from 'react-router-dom';
import {
  CalendarXIcon,
  ClockIcon,
  CrosshairIcon,
  EqualIcon,
  FlagIcon,
  ForwardIcon,
  HandGrabIcon,
  PercentIcon,
  RectangleVerticalIcon,
  ShirtIcon,
  StarIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
  TimerIcon,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { cn } from '@/lib/utils';
import { StatoCaricamento } from '../comuni/Testata.js';
import { useDati } from '../comuni/dati.js';
import { coloreVoto } from '../giocatori/colori-stato.js';
import { voce } from '../navigazione/voci.js';
import { IconaEvento } from '../partite/eventi.js';
import { Confronto, PaginaStatistiche, Riquadro, Sezione } from './PaginaStatistiche.js';
import {
  type StatisticheSquadra as Dati,
  competizioneDaSearch,
  numero,
  percentuale,
  queryCompetizione,
  quota,
} from './statistiche.js';
import { Nota } from '../comuni/Nota.js';

/** Presenze, minuti e voto non sono eventi: hanno un'icona loro, neutra, come nella scheda giocatore. */
const NEUTRA = 'size-4 shrink-0 text-evento-neutro';
const BUONO = 'bg-evento-buono';
const CATTIVO = 'bg-evento-cattivo';

export function StatisticheSquadra() {
  const { ss } = useParams<{ ss: string }>();
  const { search } = useLocation();
  const { dati, errore } = useDati<Dati>(
    `/squadre-stagione/${ss}/statistiche${queryCompetizione(competizioneDaSearch(search))}`,
  );

  return (
    <PaginaStatistiche ss={ss!} scheda="squadra" competizioni={dati?.competizioni ?? []}>
      {!dati ? (
        <StatoCaricamento errore={errore} />
      ) : dati.giocate === 0 && !dati.allenamenti?.svolti ? (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Nessuna partita conclusa</EmptyTitle>
            <EmptyDescription>
              Le statistiche contano le partite concluse: le annullate non entrano in nessun
              conteggio.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <Contenuto dati={dati} />
      )}
    </PaginaStatistiche>
  );
}

function Contenuto({ dati }: { dati: Dati }) {
  const { giocate, eventi: e } = dati;
  /** Il totale e, sotto, la media a partita. */
  const aPartita = (totale: number) =>
    giocate === 0 ? undefined : `${numero(Math.round((totale / giocate) * 10) / 10)} a partita`;
  const sulleGiocate = (n: number) =>
    giocate === 0 ? undefined : `${percentuale(quota(n, giocate - n))} delle giocate`;
  const differenza = dati.golFatti - dati.golSubiti;
  const IconaPartite = voce('partite').icona;
  const IconaAllenamenti = voce('allenamenti').icona;

  const partite = (
    <Sezione titolo="Partite" icona={<IconaPartite aria-hidden className="size-5" />}>
      <div className="grid grid-cols-2 gap-2 @3xl:grid-cols-3">
        <Riquadro
          etichetta="Giocate"
          icona={<ShirtIcon aria-hidden className={NEUTRA} />}
          valore={numero(giocate)}
        />
        <Riquadro
          etichetta="Vinte"
          icona={<ThumbsUpIcon aria-hidden className="size-4 shrink-0 text-evento-buono" />}
          valore={numero(dati.vinte)}
          sotto={sulleGiocate(dati.vinte)}
        />
        <Riquadro
          etichetta="Pareggiate"
          icona={<EqualIcon aria-hidden className={NEUTRA} />}
          valore={numero(dati.pareggiate)}
          sotto={sulleGiocate(dati.pareggiate)}
        />
        <Riquadro
          etichetta="Perse"
          icona={<ThumbsDownIcon aria-hidden className="size-4 shrink-0 text-evento-cattivo" />}
          valore={numero(dati.perse)}
          sotto={sulleGiocate(dati.perse)}
        />
        <Riquadro
          etichetta="Minuti giocati"
          icona={<ClockIcon aria-hidden className={NEUTRA} />}
          valore={numero(dati.minuti)}
          sotto={aPartita(dati.minuti)}
        />
        {/* Il Dirigente non legge i voti: il riquadro non c'e. */}
        {'votoMedio' in dati && (
          <Riquadro
            etichetta="Voto medio"
            icona={<StarIcon aria-hidden className={NEUTRA} />}
            valore={
              dati.votoMedio == null ? (
                '—'
              ) : (
                <Badge className={cn('h-8 px-2 text-xl tabular-nums', coloreVoto(dati.votoMedio))}>
                  {dati.votoMedio.toLocaleString('it-IT')}
                </Badge>
              )
            }
            sotto="Tutti i voti delle partite"
          />
        )}
      </div>
      <Nota className="text-center">
        I minuti sono quelli dei giocatori divisi per quanti vanno in campo: un'espulsione non li
        riduce.
      </Nota>
    </Sezione>
  );

  const gol = (
    <Sezione titolo="Gol" icona={<FlagIcon aria-hidden className="size-5" />}>
      <div className="grid grid-cols-2 gap-2 @3xl:grid-cols-3">
        <Riquadro
          etichetta="Gol fatti"
          icona={<IconaEvento tipo="gol" />}
          valore={numero(dati.golFatti)}
          sotto={`${numero(dati.mediaGolFatti)} a partita`}
        />
        <Riquadro
          etichetta="Gol subiti"
          icona={<IconaEvento tipo="gol_subito" />}
          valore={numero(dati.golSubiti)}
          sotto={`${numero(dati.mediaGolSubiti)} a partita`}
        />
        <Riquadro
          etichetta="Differenza reti"
          icona={<EqualIcon aria-hidden className={NEUTRA} />}
          valore={differenza > 0 ? `+${differenza}` : numero(differenza)}
        />
        <Riquadro
          etichetta="Assist"
          icona={<IconaEvento tipo="assist" />}
          valore={numero(e.assist)}
          sotto={aPartita(e.assist)}
        />
        <Riquadro
          etichetta="Autogol"
          icona={<IconaEvento tipo="autogol" />}
          valore={numero(e.autogol)}
          sotto="Contano tra i subiti"
        />
        <Confronto
          etichetta="Fatti sul totale"
          icona={<PercentIcon aria-hidden className={NEUTRA} />}
          quota={quota(dati.golFatti, dati.golSubiti)}
          a={{ etichetta: 'fatti', valore: dati.golFatti, colore: BUONO }}
          b={{ etichetta: 'subiti', valore: dati.golSubiti, colore: CATTIVO }}
        />
      </div>
    </Sezione>
  );

  const gemelli = [
    {
      titolo: 'Tiri',
      a: {
        tipo: 'tiro_specchio',
        etichetta: 'In porta',
        breve: 'in porta',
        n: e.tiriSpecchio,
      },
      b: { tipo: 'tiro_fuori', etichetta: 'Fuori', breve: 'fuori', n: e.tiriFuori },
      confronto: 'Precisione al tiro',
      Icona: CrosshairIcon,
    },
    {
      titolo: 'Passaggi',
      a: {
        tipo: 'passaggio_completato',
        etichetta: 'Riusciti',
        breve: 'riusciti',
        n: e.passaggiCompletati,
      },
      b: {
        tipo: 'passaggio_sbagliato',
        etichetta: 'Sbagliati',
        breve: 'sbagliati',
        n: e.passaggiSbagliati,
      },
      confronto: 'Precisione nei passaggi',
      Icona: ForwardIcon,
    },
    {
      titolo: 'Possesso',
      a: {
        tipo: 'palla_conquistata',
        etichetta: 'Recuperate',
        breve: 'recuperate',
        n: e.palleConquistate,
      },
      b: { tipo: 'palla_persa', etichetta: 'Perse', breve: 'perse', n: e.pallePerse },
      confronto: 'Recuperate sul totale',
      Icona: HandGrabIcon,
    },
  ] as const;

  const eventi = (
    <Sezione
      titolo="Eventi"
      icona={<TimerIcon aria-hidden className="size-5" />}
      className="@5xl:col-span-2"
    >
      <div className="grid gap-4 @3xl:grid-cols-2 @7xl:grid-cols-4">
        {gemelli.map((g) => (
          <section key={g.titolo} className="flex flex-col gap-2">
            <h3 className="text-center text-sm font-semibold text-muted-foreground">{g.titolo}</h3>
            <div className="grid grid-cols-2 gap-2">
              {[g.a, g.b].map((x) => (
                <Riquadro
                  key={x.tipo}
                  etichetta={x.etichetta}
                  icona={<IconaEvento tipo={x.tipo} />}
                  valore={numero(x.n)}
                  sotto={aPartita(x.n)}
                />
              ))}
            </div>
            <Confronto
              etichetta={g.confronto}
              icona={<g.Icona aria-hidden className={NEUTRA} />}
              quota={quota(g.a.n, g.b.n)}
              a={{ etichetta: g.a.breve, valore: g.a.n, colore: BUONO }}
              b={{ etichetta: g.b.breve, valore: g.b.n, colore: CATTIVO }}
            />
          </section>
        ))}
        <section className="flex flex-col gap-2">
          <h3 className="text-center text-sm font-semibold text-muted-foreground">Cartellini</h3>
          <div className="grid grid-cols-2 gap-2">
            <Riquadro
              etichetta="Gialli"
              icona={<IconaEvento tipo="cartellino_giallo" />}
              valore={numero(e.gialli)}
              sotto={aPartita(e.gialli)}
            />
            <Riquadro
              etichetta="Rossi"
              icona={<IconaEvento tipo="cartellino_rosso" />}
              valore={numero(e.rossi)}
              sotto={aPartita(e.rossi)}
            />
          </div>
          <Confronto
            etichetta="Cartellini totali"
            icona={<RectangleVerticalIcon aria-hidden className={NEUTRA} />}
            valore={numero(e.gialli + e.rossi)}
            quota={quota(e.gialli, e.rossi)}
            a={{ etichetta: 'gialli', valore: e.gialli, colore: 'bg-cartellino-giallo' }}
            b={{ etichetta: 'rossi', valore: e.rossi, colore: 'bg-cartellino-rosso' }}
          />
        </section>
      </div>
    </Sezione>
  );

  const a = dati.allenamenti;
  const allenamenti = a && (
    <Sezione
      titolo="Allenamenti"
      icona={<IconaAllenamenti aria-hidden className="size-5" />}
      nota="Tutta la stagione: la competizione non li filtra."
    >
      <div className="grid grid-cols-2 gap-2">
        <Riquadro
          etichetta="Svolti"
          icona={<IconaAllenamenti aria-hidden className={NEUTRA} />}
          valore={numero(a.svolti)}
        />
        <Riquadro
          etichetta="Annullati"
          icona={<CalendarXIcon aria-hidden className={NEUTRA} />}
          valore={numero(a.annullati)}
          sotto="Fuori dal conteggio"
        />
        <div className="col-span-2">
          <Riquadro
            etichetta="Partecipazione"
            icona={<PercentIcon aria-hidden className={NEUTRA} />}
            valore={percentuale(a.percentuale)}
            sotto={
              <span className="flex flex-col gap-1.5">
                <span className="flex h-2 overflow-hidden rounded-full bg-muted">
                  <span className={BUONO} style={{ width: `${a.percentuale ?? 0}%` }} />
                </span>
                Presenti sugli appelli registrati di tutta la rosa
              </span>
            }
          />
        </div>
      </div>
    </Sezione>
  );

  const massimo = Math.max(1, ...dati.perPeriodo.flatMap((p) => [p.golFatti, p.golSubiti]));
  const periodi = dati.perPeriodo.length > 0 && (
    <Sezione
      titolo="Gol per periodo"
      icona={<ClockIcon aria-hidden className="size-5" />}
      nota="Gli autogol dei nostri giocatori contano tra i subiti."
    >
      <ul className="flex flex-col gap-2">
        {dati.perPeriodo.map((p) => (
          <li
            key={p.periodo}
            className="flex items-center gap-3 rounded-lg border bg-card p-3 text-sm tabular-nums"
          >
            <span className="w-20 shrink-0 font-semibold">{p.periodo}° periodo</span>
            <span className="flex min-w-0 flex-1 flex-col gap-1.5">
              {[
                { n: p.golFatti, colore: BUONO, etichetta: 'fatti' },
                { n: p.golSubiti, colore: CATTIVO, etichetta: 'subiti' },
              ].map((r) => (
                <span key={r.etichetta} className="flex items-center gap-2">
                  <span className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                    <span
                      className={cn('block h-full', r.colore)}
                      style={{ width: `${(r.n / massimo) * 100}%` }}
                    />
                  </span>
                  <span className="w-20 text-right text-muted-foreground">
                    <span className="font-medium text-foreground">{r.n}</span> {r.etichetta}
                  </span>
                </span>
              ))}
            </span>
          </li>
        ))}
      </ul>
    </Sezione>
  );

  return (
    <div className="grid gap-4 @5xl:grid-cols-2">
      {partite}
      {gol}
      {eventi}
      {allenamenti}
      {periodi}
    </div>
  );
}
